import { useEffect, useState, type ReactNode } from 'react';
import {
  AlertTriangle,
  ArrowRight,
  CalendarClock,
  Castle,
  CheckCircle2,
  Inbox,
  RefreshCw,
  ShieldCheck,
  XCircle,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { EstadoError, EstadoVacio } from '@/components/ui/estado-vacio';
import { Skeleton } from '@/components/ui/skeleton';
import { Etiqueta, type TonoDeEtiqueta } from '@/components/ui/etiqueta';
import { cn } from '@/lib/utils';
import {
  cantidadDe,
  formatoCantidad,
  formatoNumero,
  formatoPrecio,
  haceCuanto,
} from '@/lib/utils/formato-precio';
import { usePermisosDeReglas } from '@/features/pricing-rules/lib/use-permisos-de-reglas';
import { AvisoReglasSinRecalcular } from '@/features/pricing-rules/components/aviso-reglas-sin-recalcular';
import { hrefDeLaEmpresa } from '@/router/href-de-la-empresa';
import { hrefsDeCompite } from '@/features/compite/lib/rutas';
import { CifraAnimada } from '@/features/compite/ui/cifra-animada';
import { Tarjeta, TituloDeTarjeta } from '@/features/compite/ui/tarjeta';
import { DiaUnoPanel } from './dia-uno-panel';
import { metodoEnLlano } from './ficha-estrategia';
import { usePuedeCrearEstrategias } from './nueva-estrategia/puede-crear';
import { priceProposalsApi } from '../services/price-proposals-api';
import { strategyApplicationsApi } from '../services/strategy-applications-api';
import type {
  DecisionHoy,
  DestinoHoy,
  FiltroBandeja,
  ParaSaberHoy,
  ResumenHoy,
} from '../types/price-proposals.types';

/**
 * Compite › Decisiones (antes la pestaña Hoy) — Etapa 6 Parte A (plan 173, spec
 * 156 §6), rediseñada el 14-oct con la maqueta de Melanie: arriba una franja con
 * cuántos precios esperan y cuánta plata hay en juego, y un solo botón principal;
 * abajo, por estrategia, a dónde ir a decidir; al final, lo que conviene saber.
 *
 * Lo que NO hace, a propósito: resolver acá. Cada fila lleva al lugar donde se
 * decide con el contexto completo — decidir precios desde un resumen es
 * justamente lo que Revisar precios evita.
 */

interface HoyTabProps {
  /** `filtro` abre Revisar precios ya filtrado (sin precio posible, cambios grandes). */
  onIrA: (
    destino: DestinoHoy,
    strategyApplicationId: string | null,
    filtro?: FiltroBandeja
  ) => void;
  /** Revisión t5 (m4): cambia cuando algo se resolvió afuera (la hoja de la vuelta). */
  refreshKey?: number;
}

/**
 * La etiqueta de tono de cada decisión que no es «una estrategia propone»: en
 * vez del borde rojo o ámbar de la tarjeta de antes. Un mapa y no un `if`: lo
 * que no está en el mapa va sin etiqueta.
 */
const ETIQUETA_DE: Record<string, { texto: string; tono: TonoDeEtiqueta }> = {
  SIN_PRECIO_POSIBLE: { texto: 'Conflicto entre reglas', tono: 'bloqueo' },
  // Lote C1 (punto 8): las del recálculo de productos sin estrategia.
  RECALCULO: { texto: 'Recálculo', tono: 'info' },
  VENCIDA: { texto: 'Venció', tono: 'alerta' },
  CORRIDA_FALLIDA: { texto: 'La corrida falló', tono: 'bloqueo' },
};

/**
 * El ícono de cada fila (maqueta: un cuadrito con un rayo para las estrategias).
 * El tono acompaña a la etiqueta: el color nunca es la única señal.
 */
const ICONO_DE: Record<DecisionHoy['kind'], { icono: LucideIcon; clase: string }> = {
  PROPUESTAS: { icono: Zap, clase: 'bg-primary/10 text-primary' },
  HUERFANAS: { icono: Inbox, clase: 'bg-muted text-muted-foreground' },
  SIN_PRECIO_POSIBLE: { icono: AlertTriangle, clase: 'bg-bloqueo-suave text-bloqueo' },
  RECALCULO: { icono: RefreshCw, clase: 'bg-info-suave text-info' },
  VENCIDA: { icono: CalendarClock, clase: 'bg-alerta-suave text-alerta' },
  CORRIDA_FALLIDA: { icono: XCircle, clase: 'bg-bloqueo-suave text-bloqueo' },
};

/** Las decisiones cuyo número son precios que esperan una decisión. */
const SON_POR_DECIDIR = new Set<DecisionHoy['kind']>([
  'PROPUESTAS',
  'HUERFANAS',
  'SIN_PRECIO_POSIBLE',
  'RECALCULO',
]);

/**
 * Las decisiones cuyo `detalle` dice algo que la fila no dice (por qué no hay
 * precio posible, cuándo terminó la estrategia). En las de propuestas repetía
 * la plata y el «ya no aplica»; en la corrida fallida era el código del estado.
 */
const CON_DETALLE = new Set(['SIN_PRECIO_POSIBLE', 'VENCIDA']);

/** A qué filtro de Revisar precios lleva cada decisión. */
const FILTRO_DE: Record<string, FiltroBandeja | undefined> = {
  SIN_PRECIO_POSIBLE: { sinPrecioPosible: true },
  RECALCULO: { origen: 'RECALCULO' },
};

/** Hoy muestra hasta cuatro decisiones (`MAX_DECISIONES_HOY`): alcanza con estas palabras. */
const EN_PALABRAS: Record<number, string> = { 2: 'dos', 3: 'tres', 4: 'cuatro' };
const enPalabras = (n: number) => EN_PALABRAS[n] ?? formatoCantidad(n);

/** La plata en juego de las decisiones que se ven, redondeada a centavos. */
export function sumaEnJuego(decisiones: DecisionHoy[]): number {
  return Math.round(decisiones.reduce((s, d) => s + d.plataEnJuego, 0) * 100) / 100;
}

/**
 * «Bs 758.066,82 en juego entre las tres estrategias». Sólo suma lo que se ve:
 * si hay decisiones recortadas lo dice («en esta lista»), y si hay decisiones
 * que no son de una estrategia no las llama estrategias. Sin plata, nada.
 */
export function totalEnJuego(decisiones: DecisionHoy[], recortadas: number): string | null {
  const total = sumaEnJuego(decisiones);
  if (total <= 0) return null;
  return `Bs ${formatoPrecio(total)} en juego ${dondeEnJuego(decisiones, recortadas)}`;
}

function dondeEnJuego(decisiones: DecisionHoy[], recortadas: number): string {
  const n = decisiones.length;
  if (recortadas > 0) return 'en esta lista';
  if (decisiones.every((d) => d.kind === 'PROPUESTAS')) {
    return n === 1 ? 'en esta estrategia' : `entre las ${enPalabras(n)} estrategias`;
  }
  return n === 1 ? 'en esta decisión' : `en estas ${enPalabras(n)} decisiones`;
}

export function HoyTab({ onIrA, refreshKey = 0 }: HoyTabProps) {
  const [resumen, setResumen] = useState<ResumenHoy | null>(null);
  const [error, setError] = useState<string | null>(null);
  // «Reintentar» vuelve a pedir lo de hoy.
  const [intento, setIntento] = useState(0);
  // Plan 3, T8 (ruling de la T7): con el día uno a la vista, su botón es el de crear.
  const [conDiaUno, setConDiaUno] = useState(false);
  // El método de cada estrategia («Apuntar a un margen · 27 %»), como en la
  // maqueta. Viene aparte: si no llega, la fila va sin esa línea y nada se rompe.
  const [metodos, setMetodos] = useState<Record<string, string>>({});

  useEffect(() => {
    let vivo = true;
    setError(null);
    priceProposalsApi
      .hoy()
      .then((r) => {
        if (vivo) setResumen(r);
      })
      .catch(() => {
        // Un error no puede verse como «todo al día»: eso es falsa confianza.
        if (vivo) setError('No se pudo cargar lo de hoy. Probá de nuevo en un momento.');
      });
    strategyApplicationsApi
      .listar()
      .then((lista) => {
        if (!vivo) return;
        const m: Record<string, string> = {};
        for (const e of lista) m[e.id] = metodoEnLlano(e);
        setMetodos(m);
      })
      .catch(() => {
        if (vivo) setMetodos({});
      });
    return () => {
      vivo = false;
    };
  }, [refreshKey, intento]);

  if (error) {
    return <EstadoError onReintentar={() => setIntento((n) => n + 1)}>{error}</EstadoError>;
  }

  if (!resumen) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Cargando las decisiones de hoy">
        <Skeleton className="h-[148px] w-full rounded-tarjeta" />
        <Skeleton className="h-64 w-full rounded-tarjeta" />
      </div>
    );
  }

  if (resumen.decisiones.length === 0) {
    // Ronda Y: mientras esto se ve, el recorrido de la demo ofrece «Ir a Estrategia».
    return (
      <div data-tour="demo-decisiones-sin-propuestas" className="space-y-5">
        <ReglasSinRecalcularEnHoy resumen={resumen} />
        {/* El día uno (plan 3, T7): sólo aparece sin costo ni competencia cargados. */}
        <DiaUnoPanel
          onVisible={setConDiaUno}
          hayEstrategiasActivas={resumen.hayEstrategiasActivas}
        />
        <TodoAlDia resumen={resumen} onIrA={onIrA} conDiaUno={conDiaUno} />
        {resumen.paraSaber && <ParaSaber p={resumen.paraSaber} onIrA={onIrA} />}
      </div>
    );
  }

  const total = totalEnJuego(resumen.decisiones, resumen.decisionesRecortadas);
  const maximo = Math.max(...resumen.decisiones.map((d) => d.plataEnJuego), 0);

  return (
    <div className="space-y-6">
      <ReglasSinRecalcularEnHoy resumen={resumen} />

      <FranjaDelDia
        pendientes={resumen.pendientes}
        enJuego={sumaEnJuego(resumen.decisiones)}
        donde={dondeEnJuego(resumen.decisiones, resumen.decisionesRecortadas)}
        resumenEnPalabras={total}
        onRevisar={() => onIrA('BANDEJA', null)}
      />

      <Tarjeta className="overflow-hidden">
        <div className="flex flex-wrap items-baseline justify-between gap-2 px-6 pb-4 pt-5">
          <TituloDeTarjeta como="h3">Por estrategia</TituloDeTarjeta>
          <p className="text-xs text-muted-foreground">
            En juego: la diferencia por unidad por el stock.
          </p>
        </div>
        <ul className="divide-y divide-border border-t border-border">
          {resumen.decisiones.map((d) => (
            <FilaDecision
              key={`${d.kind}-${d.strategyApplicationId ?? 'sin-estrategia'}`}
              decision={d}
              metodo={d.strategyApplicationId ? metodos[d.strategyApplicationId] : undefined}
              maximo={maximo}
              onIrA={onIrA}
            />
          ))}
        </ul>
        {resumen.decisionesRecortadas > 0 && (
          <p className="border-t border-border px-6 py-3 text-xs text-muted-foreground">
            … y{' '}
            {resumen.decisionesRecortadas === 1
              ? '1 decisión más'
              : `${num(resumen.decisionesRecortadas)} decisiones más`}
            . Están todas en Revisar precios.
          </p>
        )}
      </Tarjeta>

      {resumen.paraSaber && <ParaSaber p={resumen.paraSaber} onIrA={onIrA} />}
    </div>
  );
}

const num = formatoCantidad;

/**
 * La franja de la maqueta: cuántos precios esperan, cuánta plata hay en juego y
 * el botón principal. El degradado SÓLIDO (lleva texto blanco). Las cifras suben
 * al aparecer; con «reducir movimiento» se ven de una.
 */
function FranjaDelDia({
  pendientes,
  enJuego,
  donde,
  resumenEnPalabras,
  onRevisar,
}: {
  pendientes: number;
  enJuego: number;
  donde: string;
  resumenEnPalabras: string | null;
  onRevisar: () => void;
}) {
  return (
    <section
      aria-label="Resumen del día"
      className="relative overflow-hidden rounded-tarjeta bg-primary bg-degradado-prixia px-7 py-7 text-primary-foreground shadow-card sm:px-9 dark:text-foreground"
    >
      {/* El brillo celeste de la esquina: decoración pura. */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-prixia-sky opacity-25 blur-3xl"
      />
      {/* Lo que la franja dice con cifras, en una frase: el encabezado de la sección
          (como el «29 precios esperan tu decisión» de antes) y la plata en juego. */}
      <h2 className="sr-only">
        {pendientes === 1
          ? '1 precio espera tu decisión'
          : pendientes === 0
            ? 'Hay algo para mirar'
            : `${num(pendientes)} precios esperan tu decisión`}
      </h2>
      {resumenEnPalabras && <p className="sr-only">{resumenEnPalabras}</p>}
      <div className="relative flex flex-wrap items-center gap-x-10 gap-y-6">
        <div className="min-w-[9rem]">
          <p className="text-sm font-medium text-primary-foreground/80 dark:text-foreground/80">
            Precios por decidir
          </p>
          <CifraAnimada
            valor={pendientes}
            formato={(n) => formatoCantidad(Math.round(n))}
            className="tabular-nums mt-1 block text-5xl font-semibold leading-none"
          />
          {pendientes === 0 && (
            <p className="mt-2 text-sm text-primary-foreground/80 dark:text-foreground/80">
              Nada espera tu sí · hay algo para mirar
            </p>
          )}
        </div>
        {enJuego > 0 && (
          <>
            <span
              aria-hidden="true"
              className="hidden h-16 w-px bg-primary-foreground/25 dark:bg-foreground/25 sm:block"
            />
            <div className="min-w-0">
              <p className="text-sm font-medium text-primary-foreground/80 dark:text-foreground/80">
                En juego
              </p>
              <CifraAnimada
                valor={enJuego}
                formato={(n) => `Bs ${formatoPrecio(n)}`}
                className="tabular-nums mt-1 block text-4xl font-semibold leading-none"
              />
              <p className="mt-2 text-sm text-primary-foreground/75 dark:text-foreground/75">
                diferencia por unidad × stock · {donde}
              </p>
            </div>
          </>
        )}
        <button
          type="button"
          onClick={onRevisar}
          className="ml-auto inline-flex h-12 items-center gap-2 rounded-full bg-primary-foreground px-6 dark:bg-foreground dark:text-prixia-ink text-base font-semibold text-primary shadow-sm transition hover:bg-primary-foreground/90 dark:hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-foreground focus-visible:ring-offset-2 focus-visible:ring-offset-transparent active:scale-[0.98]"
        >
          {pendientes > 0 ? `Revisar los ${num(pendientes)}` : 'Revisar todos'}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </section>
  );
}

/**
 * D11 (lote C1): el mismo aviso de Reglas, arriba de todo (spec 275, R1-d). El
 * botón vive en Reglas: acá lleva ahí (sólo a quien ve reglas).
 */
function ReglasSinRecalcularEnHoy({ resumen }: { resumen: ResumenHoy }) {
  const { ver: veReglas, encender } = usePermisosDeReglas();
  return (
    <AvisoReglasSinRecalcular
      reglas={resumen.reglasSinRecalcular}
      cambios={resumen.cambiosSinAplicar}
      accion={
        veReglas && (
          <a
            href={hrefDeLaEmpresa(hrefsDeCompite.reglas())}
            className={cn(buttonVariants({ variant: 'outline', size: 'sm' }), 'rounded-full')}
          >
            {/* Spec 294 (D-R4-4): con cambios sin aplicar, el enlace es para aplicarlos;
                quien no puede aplicarlos (sin encender) sólo los ve (ronda de la T11, M1). */}
            {resumen.cambiosSinAplicar
              ? encender
                ? 'Aplicarlos en Reglas'
                : 'Verlos en Reglas'
              : 'Recalcular en Reglas'}
          </a>
        )
      }
    />
  );
}

/**
 * «Sony al más bajo quiere mover 4 precios más de 10 %» (maqueta S6, 4B-datos
 * T9). Sin umbral o con la estrategia borrada no se inventa el %.
 */
export function fraseDeCambioGrande(g: {
  strategyName: string | null;
  askAboveChangePct: number | null;
  cantidad: number;
}): string {
  const quien = g.strategyName ?? 'Una estrategia que ya no existe';
  const cuanto =
    g.askAboveChangePct != null
      ? `más de ${formatoNumero(g.askAboveChangePct)} %`
      : 'más de lo que le permitiste sin preguntar';
  return g.cantidad === 1
    ? `${quien} quiere mover un precio ${cuanto}`
    : `${quien} quiere mover ${num(g.cantidad)} precios ${cuanto}`;
}

/** El ícono de cada tarjeta de «para saber», según su tono. */
const ICONO_DE_TONO: Partial<Record<TonoDeEtiqueta, { icono: LucideIcon; clase: string }>> = {
  alerta: { icono: AlertTriangle, clase: 'bg-alerta-suave text-alerta' },
  info: { icono: ShieldCheck, clase: 'bg-info-suave text-info' },
  bloqueo: { icono: XCircle, clase: 'bg-bloqueo-suave text-bloqueo' },
};

/**
 * Una tarjeta de «para saber» de la maqueta: borde punteado, el ícono en su
 * cuadrito, la etiqueta al lado del título, el texto debajo y sus
 * acciones a la derecha.
 */
function TarjetaParaSaber({
  etiqueta,
  tono,
  titulo,
  children,
  acciones,
}: {
  etiqueta: string;
  tono: TonoDeEtiqueta;
  titulo: ReactNode;
  children?: ReactNode;
  acciones?: ReactNode;
}) {
  const icono = ICONO_DE_TONO[tono] ?? { icono: Inbox, clase: 'bg-muted text-muted-foreground' };
  const Icono = icono.icono;
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-tarjeta border border-dashed border-border bg-card px-6 py-5">
      <div className="flex min-w-0 items-start gap-4">
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            icono.clase
          )}
        >
          <Icono className="h-5 w-5" aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 className="flex flex-wrap items-center gap-2 text-base font-semibold text-foreground">
            <Etiqueta tono={tono}>{etiqueta}</Etiqueta> <span>{titulo}</span>
          </h3>
          {children && (
            <div className="mt-1 space-y-0.5 text-sm text-muted-foreground">{children}</div>
          )}
        </div>
      </div>
      {acciones && <div className="flex shrink-0 flex-wrap items-center gap-2">{acciones}</div>}
    </div>
  );
}

/** El estilo de los botones de «para saber»: borde y píldora, como en la maqueta. */
const BOTON_PARA_SABER = 'rounded-full';

/**
 * Lo que es para saber (maqueta S6): los cambios grandes que esperan, lo que
 * cuidaron las reglas, los productos sin costo. Del 4B-datos, Tarea 9.
 */
function ParaSaber({ p, onIrA }: { p: ParaSaberHoy; onIrA: HoyTabProps['onIrA'] }) {
  // Ruling R6, con los permisos del 26-sep: el enlace a Reglas sólo con ver
  // reglas. Sin él la página no se abre (la foto da 403): se dice el número,
  // sin el enlace.
  const { ver: veReglas } = usePermisosDeReglas();
  const tipos = p.cuidaron?.porTipo ?? {};
  const partes = [
    tipos.MARGEN_MINIMO ? `${num(tipos.MARGEN_MINIMO)} por margen mínimo` : null,
    tipos.MOVIMIENTO ? `${num(tipos.MOVIMIENTO)} por moverse` : null,
    tipos.SOBRE_EL_MAS_BARATO
      ? `${num(tipos.SOBRE_EL_MAS_BARATO)} por quedar cerca del más barato`
      : null,
    // Lote B2 (D7): el espejo, debajo del más barato.
    tipos.BAJO_EL_MAS_BARATO
      ? `${num(tipos.BAJO_EL_MAS_BARATO)} por no quedar muy debajo del más barato`
      : null,
    // Los siete tipos de límite: uno que falte acá desaparece callado de la frase.
    tipos.MARGEN_MAXIMO ? `${num(tipos.MARGEN_MAXIMO)} por margen máximo` : null,
    tipos.PISO_FIJO ? `${num(tipos.PISO_FIJO)} por su piso en Bs` : null,
    tipos.TECHO_FIJO ? `${num(tipos.TECHO_FIJO)} por su techo en Bs` : null,
  ].filter(Boolean);
  // `porTipo` cuenta productos por tipo de límite (limitaron.service.ts): uno que
  // limitaron dos tipos cuenta en los dos, y las partes pueden sumar más que el
  // total. Se dice, para que no se lea como una cuenta mal hecha (B8).
  const sumaDeTipos = Object.values(tipos).reduce<number>((s, n) => s + (n ?? 0), 0);
  const masDeUna = p.cuidaron != null && sumaDeTipos > p.cuidaron.productos;
  const estados = p.cuidaron?.porEstado ?? null;
  const dondeQuedaron = estados
    ? [
        estados.esperan > 0
          ? `${num(estados.esperan)} ${estados.esperan === 1 ? 'espera' : 'esperan'} tu decisión`
          : null,
        estados.aplicadasSolas > 0
          ? `${num(estados.aplicadasSolas)} se ${estados.aplicadasSolas === 1 ? 'aplicó solo' : 'aplicaron solos'}`
          : null,
        estados.decididas > 0
          ? `${num(estados.decididas)} ya ${estados.decididas === 1 ? 'decidido' : 'decididos'}`
          : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';
  return (
    <div className="space-y-3" aria-label="Para saber">
      {p.cambiosGrandes > 0 && (
        <TarjetaParaSaber
          etiqueta="Decidí"
          tono="alerta"
          titulo={
            <>
              {num(p.cambiosGrandes)}{' '}
              {p.cambiosGrandes === 1 ? 'cambio grande espera' : 'cambios grandes esperan'} tu sí
            </>
          }
          acciones={
            <Button
              variant="outline"
              className={BOTON_PARA_SABER}
              onClick={() => onIrA('BANDEJA', null, { motivo: 'UMBRAL' })}
            >
              Ver {p.cambiosGrandes === 1 ? 'cuál' : `los ${num(p.cambiosGrandes)}`}
            </Button>
          }
        >
          {(p.cambiosGrandesPorEstrategia ?? []).length > 0 ? (
            <ul>
              {p.cambiosGrandesPorEstrategia!.map((g) => (
                <li key={g.strategyApplicationId ?? 'ya-no-existe'}>
                  {fraseDeCambioGrande(g)}
                  {g.strategyApplicationId && (
                    <Button
                      variant="link"
                      size="sm"
                      className="h-auto px-1"
                      aria-label={`Ver los de ${g.strategyName ?? 'esa estrategia'}`}
                      onClick={() =>
                        onIrA('BANDEJA', g.strategyApplicationId, { motivo: 'UMBRAL' })
                      }
                    >
                      ver
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p>La estrategia quiere mover más de lo que le permitiste mover sin preguntar.</p>
          )}
        </TarjetaParaSaber>
      )}
      {p.cuidaron && p.cuidaron.productos > 0 && (
        <TarjetaParaSaber
          etiqueta="Para saber"
          tono="info"
          titulo={
            <>
              {p.cuidaron.dias === 7 ? 'Esta semana' : `En los últimos ${p.cuidaron.dias} días`} tus
              reglas cuidaron {cantidadDe(p.cuidaron.productos, 'precio', 'precios')}
            </>
          }
          acciones={
            // 28-sep: cada parte manda a la lista que la tiene. Antes un solo
            // «Ver cuáles» abría las aplicadas solas, y si todas esperaban
            // salía vacía. Un backend viejo (sin porEstado) sigue con ése.
            estados ? (
              <>
                {estados.esperan > 0 && (
                  <Button
                    variant="outline"
                    className={BOTON_PARA_SABER}
                    onClick={() =>
                      onIrA('BANDEJA', null, {
                        status: 'PENDING',
                        limitadaPorRegla: true,
                        dias: p.cuidaron!.dias,
                      })
                    }
                  >
                    {estados.esperan === 1 ? 'Ver el que espera' : 'Ver los que esperan'}
                  </Button>
                )}
                {estados.aplicadasSolas > 0 && (
                  <Button
                    variant="outline"
                    className={BOTON_PARA_SABER}
                    onClick={() =>
                      onIrA('BANDEJA', null, {
                        status: 'AUTO_APPLIED',
                        limitadaPorRegla: true,
                        dias: p.cuidaron!.dias,
                      })
                    }
                  >
                    {estados.aplicadasSolas === 1
                      ? 'Ver el aplicado solo'
                      : 'Ver los aplicados solos'}
                  </Button>
                )}
              </>
            ) : (
              <Button
                variant="outline"
                className={BOTON_PARA_SABER}
                onClick={() =>
                  onIrA('BANDEJA', null, {
                    status: 'AUTO_APPLIED',
                    limitadaPorRegla: true,
                    dias: p.cuidaron!.dias,
                  })
                }
              >
                Ver cuáles
              </Button>
            )
          }
        >
          {/* Maqueta index.html#hoy: «Los limitaron: … · 1 espera tu decisión», todo en una línea. */}
          {(partes.length > 0 || dondeQuedaron) && (
            <p>
              {partes.length > 0 && (
                <>
                  Los limitaron: {partes.join(' · ')}
                  {masDeUna && ' (a algunos, más de una regla)'}
                </>
              )}
              {partes.length > 0 && dondeQuedaron && ' · '}
              {dondeQuedaron}
            </p>
          )}
        </TarjetaParaSaber>
      )}
      {p.sinCosto != null && p.sinCosto > 0 && (
        <TarjetaParaSaber
          etiqueta="Falta un dato"
          tono="alerta"
          titulo={cantidadDe(p.sinCosto, 'producto sin costo', 'productos sin costo')}
          acciones={
            // Un enlace: abre Estrategia › Productos ya filtrado por «sin costo» (plan 3, P3-D2).
            veReglas && (
              <a
                href={hrefDeLaEmpresa(hrefsDeCompite.productos({ sinCosto: true }))}
                className={cn(buttonVariants({ variant: 'outline' }), BOTON_PARA_SABER)}
              >
                Ver cuáles
              </a>
            )
          }
        >
          <p>Sin costo, ninguna regla de margen los puede cuidar: en su ficha se ve —.</p>
          {/* Lote C1 (punto 10): los cargados en 0 también son «sin costo». */}
          {(p.sinCostoEnCero ?? 0) > 0 && (
            <p>
              {cantidadDe(p.sinCostoEnCero!, 'tiene', 'tienen')} el costo en 0,00:{' '}
              {p.sinCostoEnCero === 1 ? 'cuenta' : 'cuentan'} como sin costo.
            </p>
          )}
        </TarjetaParaSaber>
      )}
    </div>
  );
}

/**
 * Una fila de «Por estrategia» (maqueta): el ícono, el nombre de la estrategia
 * (o el título, si la decisión no es de una) con su método debajo, cuántos
 * precios esperan, la plata en juego con una barra para comparar de un vistazo,
 * y UNA sola acción.
 */
function FilaDecision({
  decision: d,
  metodo,
  maximo,
  onIrA,
}: {
  decision: DecisionHoy;
  /** «Apuntar a un margen · 27 %». Sin estrategia o sin respuesta, nada. */
  metodo?: string;
  /** La plata en juego más grande de la lista: el 100 % de las barras. */
  maximo: number;
  onIrA: HoyTabProps['onIrA'];
}) {
  const etiqueta = ETIQUETA_DE[d.kind];
  const icono = ICONO_DE[d.kind] ?? ICONO_DE.PROPUESTAS;
  const Icono = icono.icono;
  // La columna dice la estrategia; sin ella (huérfanas, sin precio posible, un
  // backend viejo), el título que arma el backend. En la vencida y la corrida
  // fallida el estado ya lo dice la etiqueta: el título lo repetía (auditoría M2).
  const nombre =
    d.kind === 'SIN_PRECIO_POSIBLE' || d.kind === 'HUERFANAS' || d.kind === 'RECALCULO'
      ? d.titulo
      : (d.estrategia ?? d.titulo);
  const yaNoAplican = d.yaNoAplican ?? 0;
  const frenadas = d.frenadasPorCosto ?? 0;
  const subtitulo = CON_DETALLE.has(d.kind) && d.detalle ? d.detalle : metodo;
  const proporcion = maximo > 0 ? Math.max(4, (d.plataEnJuego / maximo) * 100) : 0;
  // La maqueta dice «Revisar»: ya no hay «bandeja», es Revisar precios. El resto
  // de las acciones («Resolver», la vuelta, ver la estrategia) conserva su texto.
  const textoAccion = d.accion.label.replace(/ en la bandeja$/, '');

  return (
    <li className="flex flex-wrap items-center gap-x-8 gap-y-4 px-6 py-5">
      <div className="flex min-w-[16rem] flex-1 items-center gap-4">
        <span
          className={cn(
            'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl',
            icono.clase
          )}
        >
          <Icono className="h-5 w-5" aria-hidden="true" />
        </span>
        {/* Ronda del QA de R4 (R4-46): un nombre de 150 letras corta en su lugar. */}
        <div className="min-w-0 [overflow-wrap:anywhere]">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="tracking-tight text-lg font-semibold text-foreground">{nombre}</span>
            {etiqueta && <Etiqueta tono={etiqueta.tono}>{etiqueta.texto}</Etiqueta>}
            {yaNoAplican > 0 && d.kind !== 'VENCIDA' && (
              <Etiqueta tono="alerta">
                {yaNoAplican === 1 ? '1 ya no aplica' : `${num(yaNoAplican)} ya no aplican`}
              </Etiqueta>
            )}
            {/* Lote C1 (punto 9): las que aplica sola y el costo frenó. */}
            {frenadas > 0 && (
              <Etiqueta tono="bloqueo">
                {frenadas === 1
                  ? '1 frenada: quedaba debajo del costo'
                  : `${num(frenadas)} frenadas: quedaban debajo del costo`}
              </Etiqueta>
            )}
          </div>
          {subtitulo && <p className="mt-0.5 text-sm text-muted-foreground">{subtitulo}</p>}
        </div>
      </div>

      <div className="w-32 shrink-0">
        {d.cantidad > 0 ? (
          <>
            <p className="tabular-nums text-xl font-semibold text-foreground">
              {num(d.cantidad)} {d.cantidad === 1 ? 'precio' : 'precios'}
            </p>
            <p className="text-sm text-muted-foreground">
              {SON_POR_DECIDIR.has(d.kind) ? 'por decidir' : 'en esta decisión'}
            </p>
          </>
        ) : (
          <span className="text-muted-foreground" aria-label="Sin precios">
            —
          </span>
        )}
      </div>

      <div className="w-56 shrink-0">
        {d.plataEnJuego > 0 ? (
          <>
            <p className="tabular-nums text-base font-semibold text-foreground">
              Bs {formatoPrecio(d.plataEnJuego)}
            </p>
            <div
              className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-muted"
              aria-hidden="true"
            >
              <div className="bg-primary h-full rounded-full" style={{ width: `${proporcion}%` }} />
            </div>
          </>
        ) : (
          <>
            <span className="text-muted-foreground">—</span>
            <span className="sr-only"> sin plata en juego</span>
          </>
        )}
      </div>

      <Button
        variant="outline"
        className="ml-auto shrink-0 gap-1.5 rounded-full"
        onClick={() => {
          const filtro = FILTRO_DE[d.kind];
          if (filtro) onIrA(d.accion.destino, d.strategyApplicationId, filtro);
          else onIrA(d.accion.destino, d.strategyApplicationId);
        }}
      >
        {textoAccion}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    </li>
  );
}

function TodoAlDia({
  resumen,
  onIrA,
  conDiaUno = false,
}: {
  resumen: ResumenHoy;
  onIrA: HoyTabProps['onIrA'];
  /** El panel del día uno está a la vista (y trae su propio «Crear una estrategia»). */
  conDiaUno?: boolean;
}) {
  // Nº 12 (D5): un vendedor que toca «Crear» llega a una pestaña sin ese botón.
  const puedeCrear = usePuedeCrearEstrategias();
  if (!resumen.hayEstrategiasActivas) {
    // Ruling de la T7: con el día uno a la vista, dos «Crear una estrategia» iguales
    // uno debajo del otro confunden; queda el del panel.
    const conBoton = !(puedeCrear && conDiaUno);
    return (
      <EstadoVacio
        titulo="Todavía no hay estrategias"
        icono={Castle}
        className="rounded-tarjeta"
        accion={
          conBoton ? (
            <Button className="rounded-full" onClick={() => onIrA('ESTRATEGIAS', null)}>
              {puedeCrear ? 'Crear una estrategia' : 'Ver las estrategias'}
            </Button>
          ) : undefined
        }
      >
        Cuando crees una, acá vas a ver qué propone y cuánta plata hay en juego.
      </EstadoVacio>
    );
  }

  return (
    <EstadoVacio titulo="Todo al día." icono={CheckCircle2} className="rounded-tarjeta">
      Última revisión {haceCuanto(resumen.ultimaRevisionAt)}, vigilando{' '}
      {cantidadDe(resumen.vigilando, 'producto', 'productos')}.
    </EstadoVacio>
  );
}
